import { useBottomPanel } from '@ubnt/link-core-ui/components/BottomPanel/context/BottomPanelContext';
import { useDevicePanel } from '@ubnt/link-core-ui/components/BottomPanel/context/DevicePanelContext';
import DevicePickerImage, {
  DevicePickerImageProps,
} from '@ubnt/link-core-ui/components/BottomPanel/elements/DevicePickerImage';
import { imageSizesEnum } from '@ubnt/link-core-ui/components/BottomPanel/elements/DeviceImage';
import DevicePickerVariantsEnum from '@ubnt/link-core-ui/components/DevicePicker/DevicePickerVariantsEnum';
import TooltipDescriptionContent from '@ubnt/link-core-ui/components/DevicePicker/components/TooltipDescriptionContent';
import { RadioApTypesEnum } from '@ubnt/link-core/enums';
import { useMemo } from 'react';

import { useSelector } from 'app/store';
import { useApplicationStore } from 'modules/application/store';
import {
  devicesBottomPanelImgSrcSelector,
  devicesDeviceInstanceSelector,
  devicesDeviceRoleSelector,
  devicesCustomClientDeviceInstancesSelector,
  devicesDeviceTooltipDescriptionSelector,
  devicesDeviceVisibleHiddenByCountrySelector,
  devicesDeviceDefaultInstanceSelector,
} from 'modules/devices/slice';
import { MESH_AP_ID } from 'modules/devices/constants';
import { useDevicePicker } from 'modules/bottomPanel/hooks/useDevicePicker';
import { meshDisabledItemFn } from 'modules/bottomPanel/utils';
import classes from '../components/compositions/DevicePanel.module.scss';

const DevicePickerImageContainer = (props: Partial<DevicePickerImageProps>) => {
  const { id } = useDevicePanel();
  const { leftId, rightId } = useBottomPanel();
  const { onDeviceChange, actions, itemNotesFn, priceList, hidePrices } = useDevicePicker();

  const apId = id === rightId ? leftId : undefined;
  const isMesh = id === MESH_AP_ID;
  const apType = RadioApTypesEnum.Ptmp;

  const apDevice = useSelector((state) => (apId ? devicesDeviceInstanceSelector(state, apId) : undefined));
  const deviceDisplayName = useSelector((state) => id && devicesDeviceDefaultInstanceSelector(state, id).displayName);
  const deviceName = useSelector((state) => (id ? devicesDeviceDefaultInstanceSelector(state, id).name : undefined));
  const deviceImgSrc = useSelector((state) => id && devicesBottomPanelImgSrcSelector(state, id));
  const tooltipDescription = useSelector((state) => id && devicesDeviceTooltipDescriptionSelector(state, id));
  const role = useSelector((state) => id && devicesDeviceRoleSelector(state, id));
  const externalDevices = useSelector(devicesCustomClientDeviceInstancesSelector);
  const visibleHiddenDevices = useSelector(devicesDeviceVisibleHiddenByCountrySelector);
  const isMobile = useApplicationStore((state) => state.isMobile);

  const imageSize = isMobile ? imageSizesEnum.tiny : imageSizesEnum.small;

  const tooltipContent = useMemo(() => {
    if (tooltipDescription && !isMobile) {
      return <TooltipDescriptionContent description={tooltipDescription} />;
    }
    return undefined;
  }, [isMobile, tooltipDescription]);

  if (!id || !deviceImgSrc || !deviceDisplayName || !role) {
    return null;
  }

  return (
    <DevicePickerImage
      actions={actions}
      apDevice={apDevice}
      apType={apType}
      deviceImgSrc={deviceImgSrc}
      deviceName={deviceDisplayName}
      externalDevices={externalDevices}
      isMesh={isMesh}
      imageSize={imageSize}
      onDeviceChange={onDeviceChange}
      visibleHiddenDevices={visibleHiddenDevices}
      role={role}
      tooltipContent={tooltipContent}
      variant={DevicePickerVariantsEnum.WifiIncludes}
      classNamePopoverWrap={classes.popoverWrap}
      selectedDeviceName={deviceName}
      classNameDevicePickerList={classes.devicePickerList}
      classNameDevicePicker={classes.devicePicker}
      classNamePopoverCard={classes.popoverCard}
      disabledItemFn={isMesh ? meshDisabledItemFn : undefined}
      itemNotesFn={itemNotesFn}
      priceList={priceList}
      hidePrices={hidePrices}
      {...props}
    />
  );
};

export default DevicePickerImageContainer;
